import './GovernorsPanel.css';
import { Card } from '../../components/Card';
import { CardHeader } from '../../components/CardHeader';
import { DigitRoller } from '../../components/DigitRoller';
import { type CopyKey, t } from '../../copy/strings';
import { fmtInt } from '../../format/numbers';
import {
  type GovernorsTally,
  governorsTally,
  listOrder,
  type RaceModel,
  raceModels,
  stripOrder,
} from '../../model/governors';
import { isPreHeadline } from '../../model/headline';
import type { UfCode } from '../../model/types';
import { useConfig, useFocusUf, useHeadline, useLayout, useStatics, useTimeline, useView } from '../../state/hooks';
import { RaceList } from './RaceList';
import { UfStrip } from './UfStrip';

/** id do título (o `Card` aponta `aria-labelledby` para ele, §11.1 item 7). */
export const GOVERNORS_PANEL_TITLE_ID = 'governors-panel-title';

/**
 * Número e rótulo de um trecho da contagem ("2" + "definidos"): o texto inteiro vem do catálogo (`{N} …`) e o número
 * é separado do resto para ganhar o `DigitRoller` (Faustina 300 20) e o rótulo a tipografia pequena (§11.1 item 2).
 */
export function tallyPart(key: CopyKey, n: number): { num: string; label: string } {
  const full = t(key, { N: n });
  const num = fmtInt(n);
  return full.startsWith(num) ? { num, label: full.slice(num.length).trimStart() } : { num: '', label: full };
}

/** Texto completo da contagem para o leitor de tela (§11.1 item 7). */
export function tallySr(tally: GovernorsTally): string {
  return t('gov.tally.sr', { N: tally.decided, M: tally.counting, N1T: tally.firstRound });
}

/** Disputas da vista exibida (ao vivo ou replay); antes das 17h00 ou sem vista, só o cadastro ("sem dados", E01). */
export function useGovernorRaces(): RaceModel[] {
  const view = useView();
  const statics = useStatics();
  const pre = isPreHeadline(useHeadline());
  const places = statics.geography?.places;
  // a ordem das disputas é pelo eleitorado: o do snapshot (o 1º arquivo da noite) vale desde já; o da geografia (224
  // KB, depois do 1º número) só sem ele. Esperar a geografia reordenava as linhas aos 4 s no celular, e a repintura era
  // o LCP de #/governadores (DECISOES §38)
  const electorateOf = (uf: UfCode) =>
    view?.places[uf]?.electorate || places?.find((p) => p.code === uf)?.electorate_ref || 0;
  return raceModels({ list: statics.candidates?.governor, view: pre ? null : view, electorateOf });
}

/**
 * Cartão "Governadores" (§11.1 `GovernorsPanel`): título "Governadores" e "2º turno em {N} estados"; contagem
 * "2 definidos · 5 em apuração" (ou "Nenhum resultado definido ainda") e "{N} definidos no 1º turno"; a grade de selos
 * (`UfStrip`); um separador; e "Disputas, das mais apertadas" (`RaceList`). Só as chaves de `CandidateList.governor`
 * (nunca uma lista fixa). Desktop e TV: cartão `grow` da coluna esquerda (TV sem `Pager`, só a 1ª página); `flow`:
 * versão compacta com todas as disputas, sem `Pager`; `tablet`/`land`: 2 linhas por página com `Pager`. Carregando,
 * antes das 17h00 e sem votos: a estrutura real com o cadastro (selos "sem dados", linhas em espera). Replay: sobre o
 * snapshot exibido, sem FLIP, sem lavagem e sem rolagem de dígitos. Sem `features.governors`, nada (a aba não existe).
 */
export function GovernorsPanel() {
  const config = useConfig();
  const { mode } = useLayout();
  const { live } = useTimeline();
  const current = useFocusUf();
  const races = useGovernorRaces();
  if (!config.features.governors) return null;

  const replay = !live;
  const tally = governorsTally(races);
  const decided = tallyPart('gov.tally.elected', tally.decided);
  const counting = tallyPart('gov.tally.counting', tally.counting);
  const desktopLike = mode === 'desktop' || mode === 'tv';

  return (
    <Card
      variant={desktopLike ? 'grow' : undefined}
      class={`governors-panel${mode === 'flow' ? ' governors-panel--compact' : ''}`}
      data-testid="governors-panel"
      labelledBy={GOVERNORS_PANEL_TITLE_ID}
    >
      <CardHeader
        title={t('gov.title')}
        titleId={GOVERNORS_PANEL_TITLE_ID}
        aside={t('gov.aside', { N: tally.total })}
      />
      <div class="governors-panel__tally" data-testid="governors-tally">
        <p class="governors-panel__count" data-testid="governors-count" aria-hidden="true">
          {tally.decided === 0 ? (
            <span class="governors-panel__none">{t('gov.tally.none')}</span>
          ) : (
            <>
              <span class="governors-panel__part" data-part="decided">
                <DigitRoller text={decided.num} animate={!replay} class="governors-panel__num" />{' '}
                <span class="governors-panel__label">{decided.label}</span>
              </span>
              {tally.counting > 0 ? (
                <>
                  <span class="governors-panel__sep"> · </span>
                  <span class="governors-panel__part" data-part="counting">
                    <DigitRoller text={counting.num} animate={!replay} class="governors-panel__num" />{' '}
                    <span class="governors-panel__label">{counting.label}</span>
                  </span>
                </>
              ) : null}
            </>
          )}
        </p>
        {tally.firstRound > 0 ? (
          <p class="governors-panel__first-round" data-testid="governors-first-round" aria-hidden="true">
            {t('gov.tally.firstRound', { N: tally.firstRound })}
          </p>
        ) : null}
        <p class="sr-only" data-testid="governors-tally-sr">
          {tallySr(tally)}
        </p>
        <UfStrip races={stripOrder(races)} current={current} replay={replay} />
      </div>
      <hr class="governors-panel__rule" />
      <RaceList
        races={listOrder(races)}
        current={current}
        replay={replay}
        photos={config.features.photos}
        mode={mode}
      />
    </Card>
  );
}
